import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Building2, ChevronDown, ChevronRight, X, Cloud, CloudOff, ArrowLeft } from 'lucide-react'
import { useAuth } from '../../contexts/AuthContext'
import { useClient } from '../../contexts/ClientContext'

const GstSidebar = ({ onSidebarStateChange, onNavigationAttempt }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { activeClient } = useClient()
  const [isOpen, setIsOpen] = useState(false)
  const [isMobile, setIsMobile] = useState(false)
  const [expandedItem, setExpandedItem] = useState('GST Returns')
  const [hovered, setHovered] = useState(false)
  const [keepOpen, setKeepOpen] = useState(false)

  // Menu items
  const menuItems = [
    {
      id: 'GST Returns',
      label: 'GST Returns',
      icon: Building2,
      subItems: [
        { id: 'gstr7', label: 'GSTR7', path: '/gst-dashboard/gstr7' },
        { id: 'gstr1', label: 'GSTR1', path: '/gst-dashboard/gstr1' },
        { id: 'gstr3b', label: 'GSTR3B', path: '/gst-dashboard/gstr3b' }
      ]
    },
    {
      id: 'upload-return',
      label: 'Upload Return',
      icon: Building2,
      path: '/gst-dashboard/upload-return'
    }
  ]

  // Mobile detection
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024)
    }

    checkMobile()
    window.addEventListener('resize', checkMobile)

    return () => window.removeEventListener('resize', checkMobile)
  }, [])

  // Sidebar state management
  useEffect(() => {
    if (onSidebarStateChange) {
      const sidebarOpenState = isMobile ? isOpen : true
      onSidebarStateChange({
        isExpanded: isMobile ? isOpen : (keepOpen || hovered),
        isOpen: sidebarOpenState
      })
    }
  }, [isOpen, isMobile, keepOpen, hovered, onSidebarStateChange])

  // Auto-expand on desktop
  useEffect(() => {
    if (!isMobile) {
      setKeepOpen(true)
    }
  }, [isMobile])

  const handleSubItemClick = (subItem) => {
    if (onNavigationAttempt) {
      onNavigationAttempt(subItem.path)
    } else {
      navigate(subItem.path)
    }
    if (isMobile) {
      setIsOpen(false)
    }
  }

  const toggleSidebar = () => {
    if (isMobile) {
      setIsOpen(!isOpen)
    } else {
      setKeepOpen(!keepOpen)
    }
  }

  return (
    <>
      {/* Mobile overlay */}
      {isMobile && isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg h-screen flex flex-col transition-all duration-300 ${isMobile ? (isOpen ? 'translate-x-0' : '-translate-x-full') : 'translate-x-0'
          }`}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">GST Returns</h2>
            <button
              onClick={toggleSidebar}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 lg:hidden"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {activeClient && (
            <div className="mt-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
              <div className="text-xs text-gray-500 mb-1">Active Client</div>
              <div className="text-sm font-semibold text-gray-900 truncate">{activeClient.name}</div>
              <button 
                onClick={() => navigate('/clients')}
                className="mt-2 text-xs flex items-center text-purple-600 hover:text-purple-700 transition-colors"
              >
                <ArrowLeft className="w-3 h-3 mr-1" /> Switch Client
              </button>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="mt-4 flex-1 overflow-y-auto">
          {menuItems.map((item) => (
            <div key={item.id} className="px-6 py-2">
              <button
                onClick={() => {
                  if (item.subItems) {
                    setExpandedItem(expandedItem === item.id ? null : item.id)
                  } else if (item.path) {
                    if (onNavigationAttempt) {
                      onNavigationAttempt(item.path)
                    } else {
                      navigate(item.path)
                    }
                  }
                }}
                className="flex items-center justify-between w-full text-left text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-md px-4 py-3"
              >
                <div className="flex items-center">
                  <item.icon className="w-4 h-4 mr-3" />
                  <span>{item.label}</span>
                </div>
                {item.subItems && (
                  expandedItem === item.id ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )
                )}
              </button>

              {expandedItem === item.id && item.subItems && (
                <div className="mt-2 space-y-1">
                  {item.subItems.map((subItem) => {
                    const isActive = location.pathname === subItem.path
                    return (
                      <button
                        key={subItem.id}
                        onClick={() => handleSubItemClick(subItem)}
                        className={`w-full flex items-center px-4 py-3 text-sm rounded-md transition-colors ${isActive
                            ? 'bg-purple-100 text-purple-700 font-medium'
                            : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                      >
                        <span className="ml-7">{subItem.label}</span>
                      </button>
                    )
                  })}
                </div>
              )}
            </div>
          ))}
        </nav>

        {/* Auth Status Footer */}
        <div className="p-4 border-t border-gray-200">
          {user && user.isPremium ? (
            <div className="flex items-center space-x-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <Cloud className="w-5 h-5" />
              <div className="flex flex-col">
                <span className="text-xs font-bold uppercase">Cloud Sync Active</span>
                <span className="text-xs text-green-700 truncate w-40">{user.email}</span>
              </div>
            </div>
          ) : (
            <button
              onClick={() => navigate('/premium-auth')}
              className="flex items-center justify-center w-full space-x-2 text-purple-600 bg-purple-50 hover:bg-purple-100 p-3 rounded-lg transition-colors"
            >
              <CloudOff className="w-5 h-5" />
              <span className="text-sm font-semibold">Enable Cloud Sync</span>
            </button>
          )}
        </div>
      </div>
    </>
  )
}

export default GstSidebar
